import { useQuery } from "@tanstack/react-query";
import { RefreshCcw } from "lucide-react";
import { useState } from "react";
import { api } from "../lib/api";
import { Badge, Button, Loading, PageHeader } from "../components/ui";

/** Mirrors GET /api/v1/channel-funnel/overview (src/core/application/channel-funnel-service.ts). */
export interface FunnelRow {
  key: string; label: string; snapshotAt: string | null;
  impressions: number | null; clicks: number | null; spendUsd: number | null;
  leads: number; paid: number; revenueUsd: number | null; revenueUsesConfiguredPrice: boolean;
  ctr: number | null; cvr: number | null; leadPerClick: number | null; cpl: number | null; cac: number | null; roas: number | null;
}
export interface FunnelTotal extends FunnelRow { partial: boolean; blank: { impressions: string[]; clicks: string[]; spendUsd: string[] } }
export interface ChannelFunnelOverview {
  config: { campaign: string | null; priceUsd: number | null; monthlyPaidCap: number | null };
  rows: FunnelRow[];
  total: FunnelTotal;
  history: { snapshotAt: string; rows: FunnelRow[]; total: FunnelTotal }[];
  monthlyCap: { month: string | null; paid: number; cap: number | null; reached: boolean; exceeded: boolean; paidAtMissing: number };
  generatedAt: string;
}

/** null is unknown and always shows as an em dash, never 0. */
export function fmt(value: number | null, kind: "pct" | "usd" | "x" | "n" = "n"): string {
  if (value === null || !Number.isFinite(value)) return "—";
  if (kind === "pct") return `${(value * 100).toFixed(1)}%`;
  if (kind === "usd") return `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (kind === "x") return `${value.toFixed(2)}x`;
  return value.toLocaleString("en-US");
}

const big = { fontSize: 18, lineHeight: 1.5 } as const;
const cell = { padding: "10px 14px", textAlign: "right" as const, whiteSpace: "nowrap" as const };
const head = { ...cell, fontSize: 14, textTransform: "uppercase" as const, letterSpacing: 0.5 };

function labelOf(rows: FunnelRow[], key: string) { return rows.find((r) => r.key === key)?.label ?? key; }

export function FunnelTable({ rows, total, caption }: { rows: FunnelRow[]; total: FunnelTotal; caption: string }) {
  const line = (r: FunnelRow, isTotal = false) => (
    <tr key={isTotal ? "__total" : r.key} style={isTotal ? { fontWeight: 700, borderTop: "2px solid currentColor" } : undefined}>
      <th scope="row" style={{ ...cell, textAlign: "left" }}>{isTotal ? "Total" : r.label}{r.revenueUsesConfiguredPrice ? " *" : ""}</th>
      <td style={cell}>{fmt(r.impressions)}</td><td style={cell}>{fmt(r.clicks)}</td><td style={cell}>{fmt(r.spendUsd, "usd")}</td>
      <td style={cell}>{fmt(r.leads)}</td><td style={cell}>{fmt(r.paid)}</td>
      <td style={cell}>{fmt(r.ctr, "pct")}</td><td style={cell}>{fmt(r.cvr, "pct")}</td>
      <td style={cell}>{fmt(r.cpl, "usd")}</td><td style={cell}>{fmt(r.cac, "usd")}</td><td style={cell}>{fmt(r.roas, "x")}</td>
    </tr>
  );
  return <div style={{ overflowX: "auto" }}>
    <table style={{ ...big, borderCollapse: "collapse", width: "100%" }}>
      <caption style={{ textAlign: "left", fontSize: 20, fontWeight: 700, padding: "8px 0" }}>{caption}</caption>
      <thead><tr>
        <th scope="col" style={{ ...head, textAlign: "left" }}>Ad / channel</th>
        {["Impr.", "Clicks", "Spend", "Leads", "Paid", "CTR", "CVR", "CPL", "CAC", "ROAS"].map((h) => <th key={h} scope="col" style={head}>{h}</th>)}
      </tr></thead>
      <tbody>{rows.map((r) => line(r))}{line(total, true)}</tbody>
    </table>
  </div>;
}

export function PartialNotice({ total, rows }: { total: FunnelTotal; rows: FunnelRow[] }) {
  if (!total.partial) return null;
  const parts = ([["Impressions", total.blank.impressions], ["Clicks", total.blank.clicks], ["Spend", total.blank.spendUsd]] as const)
    .filter(([, keys]) => keys.length > 0)
    .map(([name, keys]) => `${name}: ${keys.map((k) => labelOf(rows, k)).join(", ")}`);
  return <div role="note" className="architecture-note" style={big}>
    <div><strong>Partial total</strong>: blank values are unknown, not 0. Missing data from {parts.join(" · ")}.</div>
  </div>;
}

export function CapNotice({ cap }: { cap: ChannelFunnelOverview["monthlyCap"] }) {
  if (cap.cap === null) return <p style={big}>No monthly paid cap configured.</p>;
  const tone = cap.exceeded ? "danger" : cap.reached ? "warning" : "success";
  const text = cap.exceeded ? "Monthly cap EXCEEDED" : cap.reached ? "Monthly cap reached: stop taking paid bookings" : "Within monthly cap";
  return <div role={cap.reached ? "alert" : "status"} style={{ ...big, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
    <Badge tone={tone}>{text}</Badge>
    <span>{cap.paid} / {cap.cap} paid in {cap.month ?? "—"} (counted by payment date)</span>
    {cap.paidAtMissing > 0 && <span>· {cap.paidAtMissing} without paid_at counted by lead date</span>}
  </div>;
}

export function ChannelFunnelView({ data }: { data: ChannelFunnelOverview }) {
  const usesPrice = [...data.rows, data.total].some((r) => r.revenueUsesConfiguredPrice);
  return <div style={big}>
    <section className="panel" style={{ padding: 20, marginBottom: 20 }}><CapNotice cap={data.monthlyCap} /></section>
    <section className="panel" style={{ padding: 20, marginBottom: 20 }}>
      <FunnelTable rows={data.rows} total={data.total} caption="Latest snapshot per ad" />
      <PartialNotice total={data.total} rows={data.rows} />
      {usesPrice && <p>* Revenue uses the configured price for paid leads without paid_amount.</p>}
      {data.rows.length === 0 && <p>No snapshots imported yet. Import the channel funnel CSV to see data.</p>}
    </section>
    <section className="panel" style={{ padding: 20 }}>
      <h2 style={{ fontSize: 22 }}>Snapshot history</h2>
      {data.history.length === 0 ? <p>No history yet.</p> : [...data.history].reverse().map((h) => <div key={h.snapshotAt} style={{ marginBottom: 24 }}>
        <FunnelTable rows={h.rows} total={h.total} caption={`Snapshot ${h.snapshotAt} (CT)`} />
        <PartialNotice total={h.total} rows={h.rows} />
      </div>)}
    </section>
  </div>;
}

export function ChannelFunnelPage() {
  const [month, setMonth] = useState("");
  const query = useQuery({
    queryKey: ["channel-funnel-overview", month],
    queryFn: () => api<ChannelFunnelOverview>(`/api/v1/channel-funnel/overview${/^\d{4}-(0[1-9]|1[0-2])$/.test(month) ? `?month=${month}` : ""}`),
  });
  return <>
    <PageHeader eyebrow="Channel Funnel · #88" title="Channel Funnel"
      description={query.data?.config.campaign ? `Campaign ${query.data.config.campaign}. Blank = unknown, shown as —.` : "Blank = unknown, shown as —."}
      actions={<>
        <label style={{ fontSize: 16 }}>Cap month <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} style={{ fontSize: 16 }} /></label>
        <Button variant="secondary" onClick={() => query.refetch()} disabled={query.isFetching}><RefreshCcw size={16} /> Refresh</Button>
      </>} />
    {query.isPending ? <Loading /> : query.isError ? <div className="error-panel">Unable to load channel funnel: {query.error.message}</div> : <ChannelFunnelView data={query.data} />}
  </>;
}
